/**
 * Graph Data Loader
 * Loads graph data from processed files
 */

export const loadGraphData = async () => {
  try {
    // In production, this would fetch from your API or R2 bucket
    // For now, we'll load from the local processed directory
    const response = await fetch('/data/processed/graph_for_visualizer.json');
    
    if (!response.ok) {
      throw new Error(`Failed to load graph data: ${response.statusText}`);
    }

    const data = await response.json();

    // Validate data structure
    if (!data.nodes || !data.edges) {
      throw new Error('Invalid graph data structure');
    }

    console.log(`📊 Loaded graph: ${data.nodes.length} nodes, ${data.edges.length} edges`);

    return data;
  } catch (error) {
    console.error('Error loading graph data:', error);
    
    // Fallback: try alternative paths
    try {
      const response = await fetch('../data/processed/graph_for_visualizer.json');
      if (response.ok) {
        return await response.json();
      }
    } catch (e) {
      // Ignore fallback errors
    }

    throw new Error('Could not load graph data. Make sure the extraction pipeline has been run.');
  }
};

/**
 * Load additional metadata files
 */
export const loadMetadata = async () => {
  try {
    const [geometryRes, topicsRes, invariantsRes] = await Promise.all([
      fetch('/data/processed/geometry_map.json').catch(() => null),
      fetch('/data/processed/master_topics.json').catch(() => null),
      fetch('/data/processed/invariants.json').catch(() => null)
    ]);

    const metadata = {};

    if (geometryRes && geometryRes.ok) {
      metadata.geometry = await geometryRes.json();
    }

    if (topicsRes && topicsRes.ok) {
      metadata.topics = await topicsRes.json();
    }

    if (invariantsRes && invariantsRes.ok) {
      metadata.invariants = await invariantsRes.json();
    }

    return metadata;
  } catch (error) {
    console.warn('Could not load metadata files:', error);
    return {};
  }
};

/**
 * Search nodes by keyword
 */
export const searchNodes = async (query) => {
  try {
    const response = await fetch('/data/processed/semantic_index.jsonl');
    if (!response.ok) return [];

    const text = await response.text();
    const lines = text.split('\n').filter(line => line.trim());
    
    const results = [];
    const lowerQuery = query.toLowerCase();

    for (const line of lines) {
      const entry = JSON.parse(line);
      if (entry.term.toLowerCase().includes(lowerQuery)) {
        results.push(entry);
      }
    }

    return results;
  } catch (error) {
    console.error('Error searching nodes:', error);
    return [];
  }
};

/**
 * Get node by ID
 */
export const getNodeById = async (nodeId) => {
  try {
    const response = await fetch('/data/processed/nodes.jsonl');
    if (!response.ok) return null;

    const text = await response.text();
    const lines = text.split('\n').filter(line => line.trim());

    for (const line of lines) {
      const node = JSON.parse(line);
      if (node.id === nodeId) {
        return node;
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting node:', error);
    return null;
  }
};